/*
 * Notifications — state
 *
 * Drop-in replacement for `react-redux-notify`, which was abandoned upstream (last publish
 * 2022-06-26, terminal version 4.2.1) and pinned peers at `react >= 16 < 17`,
 * `react-redux >= 4 < 6` and `redux >= 3 < 4`. This project runs React 18 / react-redux 8 /
 * redux 4, so every install resolved those peers as invalid and `npm audit fix` aborted with
 * ERESOLVE before it could patch anything else in the tree.
 *
 * The exported surface is deliberately identical to the parts of react-redux-notify the app
 * actually used — `createNotification`, `NOTIFICATION_TYPE_*` and the default reducer export
 * — so the 39 existing call sites keep working unchanged.
 *
 * This module holds state only and imports neither React nor MUI. It is pulled into the
 * eager bundle by the store setup in `src/index.js` and by every action creator, so keeping
 * it free of UI imports is what stops the notification UI from landing in `main.js`. The
 * component lives in `./Notify` and is loaded lazily — see the note there.
 */

// Notification types. Values match the strings react-redux-notify used, so anything that
// compared against them literally continues to behave the same way.
export const NOTIFICATION_TYPE_SUCCESS = 'SUCCESS';
export const NOTIFICATION_TYPE_WARNING = 'WARNING';
export const NOTIFICATION_TYPE_INFO = 'INFO';
export const NOTIFICATION_TYPE_ERROR = 'ERROR';

// Action types
export const ADD_NOTIFICATION = 'ADD_NOTIFICATION';
export const REMOVE_NOTIFICATION = 'REMOVE_NOTIFICATION';
export const REMOVE_ALL_NOTIFICATIONS = 'REMOVE_ALL_NOTIFICATIONS';

// Action creators
//
// A monotonic counter rather than the upstream `parseInt(Math.random()...)`, which could
// collide between two notifications raised in the same tick and made `key` unstable.
let nextId = 0;

export const createNotification = (notification) => {
  nextId += 1;
  return {
    type: ADD_NOTIFICATION,
    notification: { id: nextId, ...notification }
  };
};

export const removeNotification = (id) => ({
  type: REMOVE_NOTIFICATION,
  id
});

export const removeAllNotifications = (force) => ({
  type: REMOVE_ALL_NOTIFICATIONS,
  force
});

// Reducer
export const initialState = [];

export default function notifyReducer(state = initialState, action = {}) {
  switch (action.type) {
    case ADD_NOTIFICATION:
      return [...state, action.notification];
    case REMOVE_NOTIFICATION:
      return state.filter((item) => item.id !== action.id);
    case REMOVE_ALL_NOTIFICATIONS:
      // Matches upstream: an unforced clear leaves notifications the user cannot dismiss.
      return action.force ? [] : state.filter((item) => !item.canDismiss);
    default:
      return state;
  }
}
